import {
    isValidElement,
    cloneElement,
    Children,
    type FC
} from "react";
import {
    View
} from "react-native";
import type ICardActionProps from "./type";
import stylesheet, {
    CARD_ACTION_SKELETON_RATIO,
    getCardActionRadius,
    useStyles
} from "./stylesheet";
import {
    NCoreUIKitTheme
} from "../../../../core/hooks";
import {
    usePointerType
} from "../../../../helpers/pointer";
import type IButtonProps from "../../../button/type";
import type {
    CardPartContentProps
} from "../../type";
import {
    withCardContext
} from "../../context";
import Skeleton from "../../../skeleton";
import Button from "../../../button";

const CardActionContent: FC<CardPartContentProps<ICardActionProps>> = ({
    customTheme: customThemeProp,
    children,
    context,
    style,
    ...props
}) => {
    const customTheme = customThemeProp ?? context.customTheme;

    const {
        controlHeights,
        touchTargets,
        typography,
        radiuses,
        borders,
        spaces
    } = NCoreUIKitTheme.useContext(customTheme);

    const pointerType = usePointerType();

    const actionRadius = getCardActionRadius({
        inset: context.inset,
        controlHeights,
        typography,
        radiuses,
        borders
    });

    const {
        container: containerDynamicStyle,
        items: itemsDynamicStyle
    } = useStyles({
        controlHeights,
        touchTargets,
        pointerType,
        typography,
        spaces
    });

    const renderSkeleton = () => {
        if(!context.isLoading) {
            return null;
        }

        return <Skeleton
            width={controlHeights.small * CARD_ACTION_SKELETON_RATIO}
            height={controlHeights.small}
            customTheme={customTheme}
            radius={actionRadius}
        />;
    };

    const renderChildren = () => {
        return Children.map(children, (child) => {
            if(!isValidElement<IButtonProps>(child) || child.type !== Button) {
                return child;
            }

            return cloneElement(child, {
                spreadBehaviour: child.props.spreadBehaviour ?? "free",
                borderRadius: child.props.borderRadius ?? actionRadius,
                customTheme: child.props.customTheme ?? customTheme
            });
        });
    };

    return <View
        {...props}
        style={[
            style,
            stylesheet.container,
            context.isPressable ? stylesheet.passThrough : undefined,
            containerDynamicStyle
        ]}
        aria-busy={context.isLoading || undefined}
    >
        {renderSkeleton()}
        <View
            style={[
                stylesheet.items,
                context.isLoading ? stylesheet.hiddenItems : undefined,
                itemsDynamicStyle
            ]}
        >
            {renderChildren()}
        </View>
    </View>;
};
export default withCardContext("action", CardActionContent);
